import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { BigButton, Field, inputCls } from "@/components/ui-kit";
import { PasswordDialog } from "@/components/admin/UsersTab";

export const ADMIN_PERMS = ["stores", "users", "billing", "settings", "features", "staff", "danger"];

function AdminDialog({ edit, onClose, onSaved }) {
  const { t } = useApp();
  const [f, setF] = useState({ email: "", name: "", password: "", perms: [], disabled: false });
  useEffect(() => {
    if (edit) setF(edit === "new" ? { email: "", name: "", password: "", perms: [], disabled: false } : { ...edit, perms: edit.admin_perms || [], password: "" });
  }, [edit]);
  const isNew = edit === "new";
  const togglePerm = (p) => setF((x) => ({ ...x, perms: x.perms.includes(p) ? x.perms.filter((y) => y !== p) : [...x.perms, p] }));
  const save = async () => {
    try {
      if (isNew) await api.post("/admin/admins", { email: f.email, name: f.name, password: f.password, perms: f.perms });
      else await api.put(`/admin/admins/${edit.id}`, { name: f.name, perms: f.perms, disabled: f.disabled });
      toast.success(t("admin_saved")); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="admin-admin-dialog">
        <DialogTitle className="font-heading text-2xl">{isNew ? t("add_admin") : f.email}</DialogTitle>
        {isNew && <Field label={t("email")}><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} dir="ltr" data-testid="admin-admin-email" /></Field>}
        <Field label={t("name")}><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="admin-admin-name" /></Field>
        {isNew && <Field label={t("new_password")}><input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} dir="ltr" data-testid="admin-admin-password" /></Field>}
        <Field label={t("admin_perms_label")}>
          <div className="grid grid-cols-2 gap-2">
            {ADMIN_PERMS.map((p) => (
              <label key={p} className="flex items-center gap-2 rounded-lg border border-border p-2.5 font-semibold text-sm cursor-pointer">
                <Checkbox checked={f.perms.includes(p)} onCheckedChange={() => togglePerm(p)} data-testid={`admin-perm-${p}`} />{t(`tab_${p}`)}
              </label>
            ))}
          </div>
        </Field>
        {!isNew && (
          <label className="flex items-center justify-between rounded-xl bg-destructive/5 p-3 font-bold text-destructive">
            {t("disable_login")}<Switch checked={!!f.disabled} onCheckedChange={(v) => setF({ ...f, disabled: v })} data-testid="admin-admin-disabled" />
          </label>
        )}
        <BigButton onClick={save} disabled={!f.name || (isNew && (!f.email || f.password.length < 8))} data-testid="admin-admin-save">{t("save")}</BigButton>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminsTab() {
  const { t } = useApp();
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const load = () => api.get("/admin/admins").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const remove = async (a) => {
    if (!window.confirm(t("delete_admin_confirm"))) return;
    try { await api.delete(`/admin/admins/${a.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="admin-admins-tab">
      <BigButton onClick={() => setEdit("new")} data-testid="admin-add-admin-btn"><Plus className="w-5 h-5" />{t("add_admin")}</BigButton>
      <div className="grid md:grid-cols-2 gap-3">
        {list.map((a) => (
          <div key={a.id} className="rounded-xl bg-white border border-border p-4 card-soft space-y-2" data-testid="admin-admin-card">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#0B2A5B] text-white grid place-items-center"><ShieldCheck className="w-5 h-5" /></span>
              <div className="min-w-0 flex-1"><div className="font-bold truncate">{a.name}</div><div className="text-xs text-muted-foreground truncate" dir="ltr">{a.email}</div></div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${a.disabled ? "bg-destructive/10 text-destructive" : "bg-secondary"}`}>{a.disabled ? t("disabled_label") : t(`role_${a.role}`)}</span>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="admin-admin-perms">
              {a.role === "superadmin" ? <span className="px-2 py-0.5 rounded-full bg-[#F0444B]/10 text-[#F0444B] text-xs font-bold">{t("all_access")}</span>
                : (a.admin_perms || []).map((p) => <span key={p} className="px-2 py-0.5 rounded-full bg-[#0B2A5B]/10 text-[#0B2A5B] text-xs font-bold">{t(`tab_${p}`)}</span>)}
            </div>
            {a.role !== "superadmin" && (
              <div className="flex gap-1.5 pt-1">
                <button type="button" onClick={() => setEdit(a)} className="h-9 px-3 rounded-lg border border-border text-xs font-bold flex items-center gap-1" data-testid="admin-admin-edit"><Pencil className="w-4 h-4" />{t("edit_btn")}</button>
                <button type="button" onClick={() => setPwFor(a)} className="h-9 px-3 rounded-lg border border-border text-xs font-bold flex items-center gap-1" data-testid="admin-admin-pw"><KeyRound className="w-4 h-4" />{t("reset_password")}</button>
                <button type="button" onClick={() => remove(a)} className="h-9 px-3 rounded-lg bg-destructive/10 text-destructive text-xs font-bold flex items-center gap-1" data-testid="admin-admin-delete"><Trash2 className="w-4 h-4" />{t("del_btn")}</button>
              </div>
            )}
          </div>
        ))}
      </div>
      <AdminDialog edit={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      <PasswordDialog target={pwFor} path={pwFor ? `/admin/admins/${pwFor.id}/password` : ""} onClose={() => setPwFor(null)} />
    </div>
  );
}
